import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar,
  Save,
  Loader2,
  Wrench,
  FileText,
  Cpu,
  ChevronDown,
  UserCheck,
  AlertTriangle,
  Info,
} from "lucide-react";
import { maintenanceAPI, machinesAPI, sopsAPI, errorAPI } from "../api";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";

const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  loading,
  disabled,
  renderOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const selectedOption = options?.find((opt) => (opt.id || opt._id) === value);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          "input text-left w-full flex justify-between items-center",
          disabled && "bg-slate-100 cursor-not-allowed",
        )}
      >
        <span className={clsx(!selectedOption && "text-slate-400")}>
          {selectedOption ? renderOption(selectedOption) : placeholder}
        </span>
        <ChevronDown
          className={clsx(
            "w-4 h-4 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 mt-1 w-full card bg-white p-1 shadow-lg max-h-60 overflow-y-auto"
          >
            {loading ? (
              <div className="px-3 py-2 text-sm text-slate-500">Loading...</div>
            ) : options?.length > 0 ? (
              options.map((option) => (
                <button
                  type="button"
                  key={option.id || option._id}
                  onClick={() => {
                    onChange(option.id || option._id);
                    setIsOpen(false);
                  }}
                  className="dropdown-item w-full text-left"
                >
                  {renderOption(option)}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500">
                No options available.
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ScheduleMaintenance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    machineId:
      location.state?.machineId?._id || location.state?.machineId || "",
    sopId: "",
    technicianId: "",
    title: location.state?.bugId
      ? `Resolve Error: ${location.state.description?.substring(0, 30) || location.state.bugId.slice(-6)}...`
      : "",
    description: location.state?.description || "",
    scheduledDate: new Date().toISOString().split("T")[0],
    maintenanceType: location.state?.bugId ? "corrective" : "preventive",
    bugId: location.state?.bugId || null,
  });

  const { data: machines, isLoading: machinesLoading } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
  });

  const { data: technicians, isLoading: techsLoading } = useQuery({
    queryKey: ["technicians-list"],
    queryFn: () => maintenanceAPI.getTechnicians().then((res) => res.data.data),
  });

  const { data: sops, isLoading: sopsLoading } = useQuery({
    queryKey: ["sops-list", formData.machineId],
    queryFn: () =>
      sopsAPI
        .getAll({ machineId: formData.machineId, status: "approved" })
        .then((res) => res.data.data),
    enabled: !!formData.machineId,
  });

  const { data: openBugs, isLoading: bugsLoading } = useQuery({
    queryKey: ["open-bugs", formData.machineId],
    queryFn: () =>
      errorAPI
        .getAll({ machineId: formData.machineId, status: "Open" })
        .then((res) => res.data.data),
    enabled: !!formData.machineId,
  });

  const scheduleMutation = useMutation({
    mutationFn: (data) => maintenanceAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance"] });
      toast.success("Maintenance scheduled successfully!");
      navigate("/maintenance");
    },
    onError: (err) =>
      toast.error(
        "Failed to schedule: " + (err.response?.data?.error || err.message),
      ),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.machineId) {
      toast.error("Please select a machine");
      return;
    }
    const submissionData = { ...formData, bugId: formData.bugId || null };
    scheduleMutation.mutate(submissionData);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-12">
      <div className="flex items-center gap-4">
        <Link
          to="/maintenance"
          className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Schedule Maintenance Task
          </h1>
          <p className="text-slate-500">
            Create a new work order for preventive or corrective action.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6 space-y-5">
          <h3 className="font-bold text-lg text-slate-800 border-b pb-3 mb-4 border-slate-100">
            Task Details
          </h3>
          <div>
            <label className="label font-medium text-slate-700">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              className="input"
              placeholder="e.g., Annual Spindle Inspection"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
            />
          </div>
          <div>
            <label className="label font-medium text-slate-700">
              Description (Optional)
            </label>
            <textarea
              className="input min-h-[80px]"
              placeholder="Provide specific instructions or notes for the technician..."
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
        </div>

        <div className="card p-6 space-y-5">
          <h3 className="font-bold text-lg text-slate-800 border-b pb-3 mb-4 border-slate-100">
            Associations & Resources
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label font-medium text-slate-700">
                Machine <span className="text-red-500">*</span>
              </label>
              <CustomDropdown
                options={machines}
                value={formData.machineId}
                onChange={(id) =>
                  setFormData({
                    ...formData,
                    machineId: id,
                    sopId: "",
                    bugId: null,
                  })
                }
                placeholder="Choose machine..."
                loading={machinesLoading}
                renderOption={(m) => `${m.name} (${m.model})`}
              />
            </div>
            <div>
              <label className="label font-medium text-slate-700">
                Linked Error Log (Optional)
              </label>
              <CustomDropdown
                options={openBugs}
                value={formData.bugId}
                onChange={(id) =>
                  setFormData({
                    ...formData,
                    bugId: id,
                    maintenanceType: "corrective",
                  })
                }
                placeholder="None"
                loading={bugsLoading}
                disabled={!formData.machineId}
                renderOption={(b) => b.description.slice(0, 40) + "..."}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label font-medium text-slate-700">
                Linked SOP (Optional)
              </label>
              <CustomDropdown
                options={sops}
                value={formData.sopId}
                onChange={(id) => setFormData({ ...formData, sopId: id })}
                placeholder="None"
                loading={sopsLoading}
                disabled={!formData.machineId}
                renderOption={(s) => s.title}
              />
            </div>
            <div>
              <label className="label font-medium text-slate-700">
                Assign Technician (Optional)
              </label>
              <CustomDropdown
                options={technicians}
                value={formData.technicianId}
                onChange={(id) =>
                  setFormData({ ...formData, technicianId: id })
                }
                placeholder="Unassigned"
                loading={techsLoading}
                renderOption={(t) => `${t.firstName} ${t.lastName}`}
              />
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-5">
          <h3 className="font-bold text-lg text-slate-800 border-b pb-3 mb-4 border-slate-100">
            Scheduling & Type
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label font-medium text-slate-700">
                Scheduled Date
              </label>
              <input
                type="date"
                className="input"
                value={formData.scheduledDate}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledDate: e.target.value })
                }
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div>
              <label className="label font-medium text-slate-700">
                Maintenance Type
              </label>
              <select
                className="input"
                value={formData.maintenanceType}
                onChange={(e) =>
                  setFormData({ ...formData, maintenanceType: e.target.value })
                }
              >
                <option value="preventive">Preventive</option>
                <option value="corrective">Corrective</option>
                <option value="predictive">Predictive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800">What Happens Next?</p>
              <p className="text-sm text-blue-700 mt-0.5">
                Scheduling this task will create a work order. If a technician
                is assigned, they will be notified. Otherwise, it will appear in
                the open tasks pool.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
          <Link to="/maintenance" className="btn btn-ghost">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={scheduleMutation.isPending}
            className="btn btn-primary"
          >
            {scheduleMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Scheduling...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4" />
                Schedule Task
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ScheduleMaintenance;
